import type { Breadcrumb, DocumentItem } from './model.js';
import type { DocumentIndex } from './normalize.js';

/** Direct children of a folder, in source order. `null` returns the roots. */
export function getChildren(index: DocumentIndex, parentId: string | null): DocumentItem[] {
  const ids = index.childrenOf.get(parentId ?? null) ?? [];
  const out: DocumentItem[] = [];
  for (const id of ids) {
    const item = index.byId.get(id);
    if (item) out.push(item);
  }
  return out;
}

/** Every descendant of a folder, depth-first. */
export function getDescendants(index: DocumentIndex, parentId: string | null): DocumentItem[] {
  const out: DocumentItem[] = [];
  const stack = [...(index.childrenOf.get(parentId ?? null) ?? [])].reverse();
  const guard = new Set<string>();
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (guard.has(id)) continue;
    guard.add(id);
    const item = index.byId.get(id);
    if (!item) continue;
    out.push(item);
    const kids = index.childrenOf.get(id);
    if (kids) for (let i = kids.length - 1; i >= 0; i--) stack.push(kids[i] as string);
  }
  return out;
}

/** Ancestors of a node, outermost first, excluding the node itself. */
export function getAncestors(index: DocumentIndex, id: string | null): DocumentItem[] {
  const chain: DocumentItem[] = [];
  let cursor = id == null ? undefined : index.byId.get(id);
  const guard = new Set<string>();
  while (cursor && cursor.parentId != null && !guard.has(cursor.id)) {
    guard.add(cursor.id);
    const parent = index.byId.get(cursor.parentId);
    if (!parent) break;
    chain.unshift(parent);
    cursor = parent;
  }
  return chain;
}

/**
 * Breadcrumb trail for a folder, root first. The synthetic root is always the
 * first entry so the UI can render "Home / Engineering / Architecture".
 */
export function getBreadcrumbs(index: DocumentIndex, id: string | null, rootLabel = 'Home'): Breadcrumb[] {
  const trail: Breadcrumb[] = [{ id: null, name: rootLabel }];
  if (id == null) return trail;
  for (const ancestor of getAncestors(index, id)) trail.push({ id: ancestor.id, name: ancestor.name });
  const self = index.byId.get(id);
  if (self) trail.push({ id: self.id, name: self.name });
  return trail;
}

/** The parent id to navigate to when going up from `id`. */
export function getParentId(index: DocumentIndex, id: string | null): string | null {
  if (id == null) return null;
  return index.byId.get(id)?.parentId ?? null;
}

/** True when `candidateId` is `folderId` or lives underneath it. */
export function isWithin(index: DocumentIndex, candidateId: string, folderId: string | null): boolean {
  if (folderId == null) return true;
  if (candidateId === folderId) return true;
  return getAncestors(index, candidateId).some((a) => a.id === folderId);
}
